import Select from "react-select";
import { useContext } from "react";
import { FormikContext } from "formik";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

const defaultPortalStyles = {
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
    fontSize: 14,
    fontWeight: 400,
  }),
  menu: (base) => ({
    ...base,
    minWidth: 180,
    width: "max-content",
  }),
};

const SelectField = ({
  label,
  name,
  options = [],
  value,
  error,
  clsname = "",
  searchable = false,
  placeholder = "Select...",
  isLoading = false,
  disabled = false,
  onChange,
  onBlur,
  menuPortalTarget,
  styles: customStyles,
  isMulti = false,
}) => {
  const formik = useContext(FormikContext);

  const resolvedValue =
    value !== undefined ? value : formik && name ? formik.values[name] : "";

  const setValue = (val) => {
    if (formik && name) {
      formik.setFieldValue(name, val);
    } else if (onChange) {
      onChange(val);
    }
  };

  const setTouched = () => {
    if (formik && name) {
      formik.setFieldTouched(name, true);
    } else if (onBlur) {
      onBlur();
    }
  };

  const formattedOptions = options.map((opt) =>
    typeof opt === "string" ? { value: opt, label: opt } : opt,
  );

  const mergedStyles = customStyles
    ? { ...defaultPortalStyles, ...customStyles }
    : defaultPortalStyles;

  if (searchable) {
    const selectValue = isMulti
      ? Array.isArray(resolvedValue)
        ? resolvedValue
            .map((v) => formattedOptions.find((o) => o.value === v))
            .filter(Boolean)
        : []
      : formattedOptions.find((o) => o.value === resolvedValue) || null;

    return (
      <div className="mb-2">
        {label && <label className={`form-label ${clsname}`}>{label}</label>}
        <div style={{ position: "relative" }}>
          <Select
            options={formattedOptions}
            value={selectValue}
            onChange={(opt) => {
              if (isMulti) {
                setValue((opt || []).map((o) => o.value));
              } else {
                setValue(opt ? opt.value : "");
              }
            }}
            onBlur={setTouched}
            placeholder={placeholder}
            isClearable
            isMulti={isMulti}
            isLoading={isLoading}
            isDisabled={disabled}
            menuPortalTarget={menuPortalTarget || document.body}
            styles={mergedStyles}
          />

          {error && (
            <ErrorOutlineIcon
              style={{
                position: "absolute",
                right: "34px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#dc3545",
                fontSize: "16px",
                pointerEvents: "none",
              }}
            />
          )}
        </div>
        {error && <div className="invalid-feedback">{error}</div>}
      </div>
    );
  }

  if (isMulti) {
    const selectedValues = Array.isArray(resolvedValue) ? resolvedValue : [];

    return (
      <div className="mb-2">
        {label && <label className={`form-label ${clsname}`}>{label}</label>}
        <select
          className={`form-select custom-select-text ${error ? "is-invalid" : ""}`}
          style={{
            color: selectedValues.length ? "#212529" : "#949ca4",
          }}
          value={selectedValues}
          onChange={(e) => {
            const values = Array.from(
              e.target.selectedOptions,
              (opt) => opt.value,
            );
            setValue(values);
          }}
          onBlur={setTouched}
          disabled={disabled}
          multiple
        >
          {formattedOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && <div className="invalid-feedback">{error}</div>}
      </div>
    );
  }

  return (
    <div className="mb-2">
      {label && <label className={`form-label ${clsname}`}>{label}</label>}
      <select
        className={`form-select custom-select-text ${error ? "is-invalid" : ""}`}
        style={{
          color: resolvedValue ? "#212529" : "#949ca4",
        }}
        value={resolvedValue || ""}
        onChange={(e) => setValue(e.target.value)}
        onBlur={setTouched}
        disabled={disabled}
      >
        <option value="">{placeholder}</option>
        {formattedOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
};

export default SelectField;
